import Link from "next/link";

export type CompanionSectionHeadingProps = {
  id: string;
  kicker?: string;
  title: string;
  action?: { href: string; label: string };
  className?: string;
  /** Use "light" only when the section sits on a navy or otherwise dark card. */
  tone?: "light" | "dark";
};

/**
 * Shared kicker and heading row used across the live Companion home
 * sections, following /today's header idiom (kicker, title, hairline,
 * optional action link).
 */
export function CompanionSectionHeading({
  id,
  kicker,
  title,
  action,
  className,
  tone = "dark",
}: CompanionSectionHeadingProps) {
  const kickerClass =
    tone === "light"
      ? "text-[11px] font-semibold uppercase tracking-[1.6px] text-gold-light"
      : "text-[11px] font-semibold uppercase tracking-[1.6px] text-gold";
  const titleClass =
    tone === "light"
      ? "font-heading font-bold text-[22px] leading-tight text-white lg:text-[28px]"
      : "font-heading font-bold text-[22px] leading-tight text-navy lg:text-[28px]";
  const hairlineClass = tone === "light" ? "h-px flex-1 bg-white/20" : "h-px flex-1 bg-border";
  const actionClass =
    tone === "light"
      ? "inline-flex min-h-[44px] shrink-0 items-center text-[13px] font-semibold text-teal-light no-underline hover:text-white"
      : "inline-flex min-h-[44px] shrink-0 items-center text-[13px] font-semibold text-teal no-underline hover:text-teal-dark";

  return (
    <div className={["flex items-center justify-between gap-3", className].filter(Boolean).join(" ")}>
      <div>
        {kicker ? <p className={kickerClass}>{kicker}</p> : null}
        <h2 id={id} className={titleClass}>
          {title}
        </h2>
      </div>
      <div className={`hidden sm:block ${hairlineClass}`} aria-hidden="true" />
      {action ? (
        <Link href={action.href} className={actionClass}>
          {action.label}
        </Link>
      ) : null}
    </div>
  );
}
